import { AccessToken } from '@eagle/common';
import { Account, AccountType, AuthAction, DocumentVersionToAccept, DomainProtectedConfigResponse, OidcAuthRequest, ProtectedAuthConfig, PublicAuthConfig, User } from '@eagle/core-data-types';
import { EthingsRestClient } from '@eagle/ethings-rest-client';
import { AxiosInstance } from 'axios';
import { PropsWithChildren } from 'react';
import { Undefinable } from '../types';
import { CodedError } from '../util/error';
import { SwitchedClientTokenProvider } from './token';

export type DomainCustomConfig = Partial<Omit<DomainProtectedConfigResponse, 'auth'> & { auth: PublicAuthConfig | ProtectedAuthConfig }>;

export interface Auth extends PropsWithChildren {
  config?: OpenIdConfiguration;
  declineTermsAndConditions: () => void;
  createAuthorizeParams: (overrides?: AuthorizeParamsOverride) => OidcAuthRequest | null;
  getAccessToken: () => Undefinable<AccessToken>;
  recentAccounts: string[];
  setAccessToken: (accessToken: string) => void;
  signIn: (overrides?: AuthorizeParamsOverride) => Promise<void>;
  signOut: (signInParam?: string) => Promise<void>;
  state?: AuthenticationState;
  switchAccount: (targetAccount: string) => Promise<void>;
  switchBackToOriginalAccount: () => void;
}

export interface WebOpenIdConfiguration {
  'authorization_endpoint': string;
  'end_session_endpoint': string;
  'token_endpoint': string;
  'x_ethings_authentication_provider'?: boolean;
}

export interface OpenIdConfiguration extends PublicAuthConfig {
  authUrl: string;
  signOutUrl: string;
  tokenEndpoint: string;
  xEthingsAuthenticationProvider?: boolean;
}

export enum AuthStatus {
  AUTHENTICATED,
  CONFIG_ERROR,
  AUTHENTICATION_FAILED,
  NOT_AUTHENTICATED,
  NETWORK_ERROR,
  LOADING,
  TERMS_AND_CONDITIONS
}

export interface UserInfo {
  accountId: string;
  baseAccountId: string;
  hasRoleFunction: (...anyOfRfn: string[]) => boolean;
  type: string;
  userId: string;
}

export type AuthenticationState = AuthenticatedState | ConfigErrorState | AuthenticationFailedState | NotAuthenticatedState | NetworkErrorState | LoadingState | TermsAndConditionsState;

export interface AuthenticatedState {
  account: Account;
  accountType?: AccountType;
  axios: AxiosInstance;
  error?: CodedError;
  loading: boolean;
  protectedConfig: Partial<DomainProtectedConfigResponse>;
  restClient: EthingsRestClient;
  status: AuthStatus.AUTHENTICATED;
  switchedTokenProvider?: SwitchedClientTokenProvider;
  switchedConfig?: Partial<DomainProtectedConfigResponse>;
  timestamp: number;
  user: User;
  userInfo: UserInfo;
}

export interface ConfigErrorState {
  error: Error;
  status: AuthStatus.CONFIG_ERROR;
}

export interface AuthenticationFailedState {
  failure: AuthenticationFailure;
  status: AuthStatus.AUTHENTICATION_FAILED;
}

export interface AuthenticationFailure {
  error: string;
  errorDescription: string;
}

export interface LoadingState {
  status: AuthStatus.LOADING;
}

export interface NetworkErrorState {
  status: AuthStatus.NETWORK_ERROR;
}

export interface NotAuthenticatedState {
  error?: CodedError;
  loading: boolean;
  status: AuthStatus.NOT_AUTHENTICATED;
}

export interface TermsAndConditionsState {
  status: AuthStatus.TERMS_AND_CONDITIONS;
  termsToken: string;
  documents: DocumentVersionToAccept[];
}

export interface TokenResult {
  'id_token'?: string;
}

export interface RemoteAuthInfo {
  accessToken?: string;
  refreshToken?: string;
}

export enum SessionStorageKeys {
  SWITCHED_ACCOUNT_ID = 'switched-account-id'
}

export interface AuthorizeParamsOverride {
  redirectUri?: string;
  state?: string;
}

export type AuthorizeResponse = {
  action: AuthAction.AUTHENTICATED;
  refreshToken: string;
  accessToken: string;
} | {
  action: AuthAction.TERMS_ACCEPTANCE_REQUIRED;
  termsToken: string;
  documents: DocumentVersionToAccept[];
}
